import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AVAILABLE_COLUMNS } from 'projects/store/src/app/constants/reports.constants';
import { ClosingRecord, ColumnDef, DateRangeFilter } from 'projects/store/src/app/interfaces';

@Component({
  selector: 'app-closing-report-credits-table',
  templateUrl: './closing-report-credits-table.component.html',
  styleUrls: ['./closing-report-credits-table.component.scss']
})
export class ClosingReportCreditsTableComponent implements OnChanges, OnInit {
  @Input() records: ClosingRecord[] = [];
  @Input() availableColumns: ColumnDef[] = AVAILABLE_COLUMNS;
  @Output() downloadPdf = new EventEmitter<ClosingRecord>();
  @Output() rowClick = new EventEmitter<ClosingRecord>();
  @Output() dateFilterChange = new EventEmitter<DateRangeFilter>();
  @Output() clearDateFilter = new EventEmitter<void>();

  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  searchText: string = '';
  selectedColumns: ColumnDef[] = [];
  filteredRecords: ClosingRecord[] = [];
  rowsPerPage = 15;
  readonly rowsPerPageOptions: number[] = [10, 15, 25, 50];

  ngOnInit(): void {
    this.selectedColumns = this.normalizeSelectedColumns(this.availableColumns);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['availableColumns']) {
      this.selectedColumns = this.normalizeSelectedColumns(this.selectedColumns);
    }

    if (changes['records']) {
      this.applyFilters();
    }
  }

  filterData(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.dateFrom = null;
    this.dateTo = null;
    this.searchText = '';
    this.filteredRecords = [...this.records];
    this.clearDateFilter.emit();
  }

  onSelectedColumnsChange(columns: ColumnDef[] | null): void {
    this.selectedColumns = this.normalizeSelectedColumns(columns ?? []);
  }

  isColumnVisible(field: string): boolean {
    if (this.isLockedColumn(field)) {
      return true;
    }

    return this.selectedColumns.some((column) => column.field === field);
  }

  getStatusSeverityClass(severity: string): string {
    return severity ? `status-tag--${severity}` : '';
  }

  getAllyPaymentSeverityClass(severity: string): string {
    return severity ? `appliedPay-tag--${severity}` : '';
  }

  getAllyPaymentIcon(severity: string): string {
    return severity === 'success' ? 'pi pi-check-circle' : 'pi pi-exclamation-circle';
  }

  onDownloadPdf(record: ClosingRecord): void {
    this.downloadPdf.emit(record);
  }

  onRowClick(record: ClosingRecord): void {
    this.rowClick.emit(record);
  }

  onDateFilter(): void {
    this.dateFilterChange.emit({
      from: this.dateFrom,
      to: this.dateTo
    });
  }

  private normalizeSelectedColumns(columns: ColumnDef[]): ColumnDef[] {
    const selectedFields = new Set(columns.map((column) => column.field));
    const normalizedColumns = this.availableColumns.filter((column) =>
      column.locked || selectedFields.has(column.field)
    );

    return normalizedColumns.length ? normalizedColumns : this.availableColumns.filter((column) => column.locked);
  }

  private isLockedColumn(field: string): boolean {
    return this.availableColumns.some((column) => column.field === field && column.locked);
  }

  private applyFilters(): void {
    const search = this.searchText.trim().toLowerCase();

    this.filteredRecords = this.records.filter((record) => {
      if (!search) {
        return true;
      }

      return (
        record.contrato.toLowerCase().includes(search) ||
        record.identidad.toLowerCase().includes(search) ||
        record.cliente.toLowerCase().includes(search) ||
        record.producto.toLowerCase().includes(search) ||
        record.imei.toLowerCase().includes(search)
      );
    });
  }
}
